/**
 * Progress of the user's in-flight analysis job, for the app-wide banner + empty states: the Pro backfill (up to 1,000
 * games, 40-90 minutes) or an incremental sync of their new games (#259, the 50 newest since the newest stored one).
 *
 * The job id comes from `activeBackfillJobId` on the user record (the worker's own id only ever lands
 * in a Stripe webhook response the browser never sees). Derivation lives in `backfillProgress.ts`. Refreshing the
 * games lists when a job ends is `useRefreshGamesOnJobEnd`, called once, by the banner.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthToken } from "../components/AuthWrapper";
import { getBackfillJob, type BackfillJob } from "../utils/apiConfig";
import { queryKeys } from "../utils/queryKeys";
import { useUserProfile } from "./api/useApiHooks";
import { deriveProgress, type BackfillProgress } from "./backfillProgress";

/** Poll cadence. The worker completes ~1.8 games/min (34s/game), so 10s is responsive without being chatty. */
const POLL_MS = 10_000;

export function useBackfillJob(): BackfillProgress {
  const { isSignedIn, getAuthHeaders } = useAuthToken();
  const { data: profile } = useUserProfile();
  const jobId = profile?.activeBackfillJobId ?? null;

  const { data: job } = useQuery<BackfillJob>({
    queryKey: queryKeys.backfillJob(jobId ?? "none"),
    queryFn: () => getBackfillJob(jobId as string, getAuthHeaders),
    enabled: !!isSignedIn && !!jobId,
    // Stop once the job reaches a terminal status. A hard-killed task writes nothing, so GET /analyze-bulk derives
    // INTERRUPTED for a running job with no write for 40 min (#304); the sync rules treat it as dead from then too.
    refetchInterval: (query) =>
      deriveProgress(query.state.data, Date.now()).shouldPoll ? POLL_MS : false,
    staleTime: 0,
    // A 404 means the job was pruned or isn't ours — don't hammer it.
    retry: false,
  });

  return deriveProgress(job, Date.now());
}
